import { Injectable, Inject, signal } from '@angular/core';
import { getLocaleDirection, registerLocaleData } from '@angular/common';
import ar from '@angular/common/locales/ar';
import cs from '@angular/common/locales/cs';
import de from '@angular/common/locales/de';
import en from '@angular/common/locales/en';
import es from '@angular/common/locales/es';
import hi from '@angular/common/locales/hi';
import hu from '@angular/common/locales/hu';
import kk from '@angular/common/locales/kk';
import ky from '@angular/common/locales/ky';
import pl from '@angular/common/locales/pl';
import pt from '@angular/common/locales/pt';
import ru from '@angular/common/locales/ru';
import sl from '@angular/common/locales/sl';
import tg from '@angular/common/locales/tg';
import uz from '@angular/common/locales/uz';
import tr from '@angular/common/locales/tr';
import uk from '@angular/common/locales/uk';
import vi from '@angular/common/locales/vi';
import fr from '@angular/common/locales/fr';
import { toObservable } from '@angular/core/rxjs-interop';

import { NbLayoutDirection, NbLayoutDirectionService } from '@nebular/theme';
import { of, Observable, filter, merge, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { SntI18nModuleConfig } from './config';
import { LanguageCode, AVAILABLE_LANGUAGES_MAP, Language } from './language.model';

export const I18N_LOCALES = new Map<LanguageCode, unknown>([
  [LanguageCode.ar, ar],
  [LanguageCode.cs, cs],
  [LanguageCode.de, de],
  [LanguageCode.en, en],
  [LanguageCode.es, es],
  [LanguageCode.fr, fr],
  [LanguageCode.hi, hi],
  [LanguageCode.hu, hu],
  [LanguageCode.kk, kk],
  [LanguageCode.ky, ky],
  [LanguageCode.pl, pl],
  [LanguageCode.pt, pt],
  [LanguageCode.ru, ru],
  [LanguageCode.sl, sl],
  [LanguageCode.tg, tg],
  [LanguageCode.tr, tr],
  [LanguageCode.uk, uk],
  [LanguageCode.uz, uz],
  [LanguageCode.vi, vi],
]);

export function getLangsConsiderConfig(config: SntI18nModuleConfig) {
  let langs = AVAILABLE_LANGUAGES_MAP;

  if (Array.isArray(config.include)) {
    config.include.forEach((lang) => {
      if (!langs.has(lang.value as LanguageCode)) langs.set(lang.value as LanguageCode, lang);
    });
  }

  if (Array.isArray(config.exclude)) {
    config.exclude.forEach((lang) => {
      if (langs.has(lang as LanguageCode)) langs.delete(lang as LanguageCode);
    });
  }

  if (Array.isArray(config.langs)) {
    const langsMap = new Map<LanguageCode, Language>();

    config.langs.forEach((lang) => {
      if (langs.has(lang as LanguageCode)) {
        langsMap.set(lang as LanguageCode, langs.get(lang as LanguageCode) as Language);
      }
    });

    langs = langsMap;
  }

  return langs;
}

@Injectable({ providedIn: 'root' })
export class SntI18nService {
  langs = AVAILABLE_LANGUAGES_MAP;
  currentLang = signal<LanguageCode>(LanguageCode.en);

  get currentLocale(): string {
    return this.translateService.currentLang;
  }

  private isReady = signal(false);

  private isReady$ = toObservable(this.isReady);

  constructor(
    private translateService: TranslateService,
    private directionService: NbLayoutDirectionService,
    @Inject(SntI18nModuleConfig) private config: SntI18nModuleConfig,
  ) {
    this.langs = getLangsConsiderConfig(this.config);

    this.translateService.setDefaultLang(this.config.defaultLang as string);
    this.translateService.addLangs(Array.from(this.langs.keys()));

    if (!this.translateService.currentLang) {
      this.setLocale((this.config?.defaultLang as LanguageCode) || LanguageCode.en);
    }

    this.isReady.set(true);
  }

  getValuesAsArray(): Language[] {
    return Array.from(this.langs.values());
  }

  getValuesAsObservable(): Observable<Language[]> {
    return of(this.getValuesAsArray());
  }

  getCurrentLanguage(): Observable<Language> {
    return this.isReady$.pipe(
      filter(Boolean),
      switchMap(() => {
        return merge(
          of(this.translateService.currentLang as LanguageCode),
          this.translateService.onLangChange.pipe(map((lang) => lang.lang as LanguageCode)),
        );
      }),
      map((lang: LanguageCode) => this.langs.get(lang)),
      filter(Boolean),
    );
  }

  setLocale(localeId: LanguageCode): void {
    if (!localeId || !this.langs.has(localeId)) return;

    const locale = I18N_LOCALES.get(localeId) || en;

    registerLocaleData(locale);
    this.currentLang.set(localeId);
    this.translateService.use(localeId);
    this._setHTMLLangAttributes(localeId);
  }

  private _setHTMLLangAttributes(localeId: LanguageCode): void {
    if (!localeId) return;

    document.documentElement.lang = localeId;
    this.directionService.setDirection((getLocaleDirection(localeId) as NbLayoutDirection) || 'ltr');
  }
}
